// MapSession.js — Typed bridge between map node views and MapEditorPortal.
// Replaces the global document CustomEvent bus.

/**
 * Ticket 03 (map-node-dtg-view-audit): the request itself carries a close-time callback
 * (`onDtgRangeCommit`) rather than MapSession exposing a separate close-callback-registry API.
 * MapNodeView already has `view`/`getPos` in closure at the exact moment it builds this request
 * (inside `_openEditor()`), so it can hand MapEditorPortal a plain function that, when invoked,
 * writes the final DTG selected point back onto *this* node via the outer document's
 * EditorView — no separate keyed-by-nodeId registration/cleanup bookkeeping needed on either
 * side, and at most one map editor session is ever open at a time so there's no ambiguity about
 * which callback is "current". MapEditorPortal simply carries this callback through unchanged on
 * `session` and calls it once, on close.
 *
 * shared-map-data ticket 02: `dtgStartMs`/`dtgEndMs` no longer travel through this request —
 * that range now lives on the shared map doc's `map_meta` node (see mapSchema.js), read/written
 * directly by MapEditorPortal/MapEditorModal via the normal editorState sync. Only
 * `dtgSelectedMs` — this embed's own scrub position within that shared range — remains node-side
 * and flows through here.
 *
 * shared-map-data ticket 03: `title`/`basemapStyleId`/`activeGridId`/`overlayType` moved off the
 * shared map doc's `map_meta` node onto this mapNode's own attrs — each is seeded into the
 * session as an initial value below, and reported back live via `onWindowSettingsChange`
 * (unlike `onDtgRangeCommit`, called on every change rather than once at close — see
 * MapNodeView._onWindowSettingsChange for why these want to feel live). `currentDtgMs` (the
 * satellite-imagery cursor) is written through the same callback but has no meaningful "seed"
 * threaded in here — it's derived live from the editor's own DTG timeline, not something the
 * embed chrome shows or edits directly.
 * @typedef {{
 *   nodeId: string,
 *   viewId: string|null,
 *   dtgSelectedMs?: number|null,
 *   title?: string,
 *   basemapStyleId?: string,
 *   activeGridId?: string|null,
 *   overlayType?: string,
 *   onDtgRangeCommit?: ((dtg: { valueMs: number|null }) => void)|null,
 *   onWindowSettingsChange?: ((patch: object) => void)|null,
 * }} MapEditorSessionRequest
 */

const listeners = new Set();

/** @param {MapEditorSessionRequest} request */
export function openMapEditor(request) {
    if (!request?.nodeId) return;
    listeners.forEach((fn) => {
        try {
            fn(request);
        } catch (err) {
            console.warn('[MapSession] listener failed', err);
        }
    });
}

/**
 * Subscribe to map editor open requests.
 * @param {(request: MapEditorSessionRequest) => void} listener
 * @returns {() => void}
 */
export function subscribeMapEditorOpen(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

// ── Deep-link support (map-editor-url-state ticket 01) ─────────────────────
// The report page reflects "is the map editor open, and for which node" into a `mapNode` search
// param (see MapEditorPortal.jsx). Opening interactively (double-click) always goes through
// MapNodeView._openEditor()'s request above, which already has `view`/`getPos` in closure. A
// cold-open from that URL param (page load / in-app link / browser back-forward landing on it)
// has no such NodeView instance to borrow — only the outer report doc + its EditorView (read from
// `useReportStore`) — so it needs its own way to locate the node and build an equivalent request.

/**
 * Walks a report PM doc for the `map` node whose `nodeId` attr matches. Re-run on every call
 * (never cached) so callers always resolve against the doc's *current* shape — safe to call
 * repeatedly across a long-lived session even as unrelated edits shift positions.
 * @param {import('prosemirror-model').Node} doc
 * @param {string} nodeId
 * @returns {{pos: number, node: import('prosemirror-model').Node}|null}
 */
export function findMapNodeById(doc, nodeId) {
    if (!doc || !nodeId) return null;
    let found = null;
    doc.descendants((node, pos) => {
        if (found) return false;
        if (node.type.name === 'map' && node.attrs.nodeId === nodeId) {
            found = { pos, node };
            return false;
        }
        return true;
    });
    return found;
}

/**
 * Builds a {@link MapEditorSessionRequest} for `nodeId` by locating it fresh in `view`'s current
 * doc — the cold-open counterpart to MapNodeView._openEditor(). Returns `null` when the node
 * can't be found (deleted, wrong report, or the doc hasn't loaded yet) so the caller can strip
 * the stale URL param instead of opening a broken session.
 *
 * The write-back callbacks re-resolve the node's position via `findMapNodeById` at *invocation*
 * time rather than closing over the `pos` found here, since — unlike a NodeView's own `getPos`
 * (kept live by ProseMirror's node-view lifecycle) — a plain captured `pos` would go stale the
 * moment any earlier part of the doc changes underneath this long-lived session.
 * @param {import('prosemirror-view').EditorView} view
 * @param {string} nodeId
 * @returns {MapEditorSessionRequest|null}
 */
export function buildMapEditorRequestFromDoc(view, nodeId) {
    const found = findMapNodeById(view?.state?.doc, nodeId);
    if (!found) return null;
    const {
        viewId, dtgSelectedMs, title, basemapStyleId, activeGridId, overlayType,
    } = found.node.attrs;

    return {
        nodeId,
        viewId: viewId ?? null,
        dtgSelectedMs: dtgSelectedMs ?? null,
        title,
        basemapStyleId,
        activeGridId,
        overlayType,
        onDtgRangeCommit: (dtg = {}) => {
            const live = findMapNodeById(view.state.doc, nodeId);
            if (!live) return;
            const { valueMs = null } = dtg;
            view.dispatch(view.state.tr.setNodeMarkup(live.pos, null, {
                ...live.node.attrs, dtgSelectedMs: valueMs,
            }));
        },
        onWindowSettingsChange: (patch) => {
            const live = findMapNodeById(view.state.doc, nodeId);
            if (!live) return;
            view.dispatch(view.state.tr.setNodeMarkup(live.pos, null, { ...live.node.attrs, ...patch }));
        },
    };
}
